import { useEffect, type ReactNode } from "react";
import { motion } from "motion/react";
import { Icon } from "./components/Icon";
import { Button } from "./components/ui";
import { useMe, usePrices } from "./api/queries";
import { insideTelegram, getInitData } from "./telegram/webapp";
import { AuthError, messagesOf } from "./api/errors";
import { slow, ui } from "./motion";
import { suggestLang } from "./i18n/lang";
import { isLatinScript } from "./i18n/format";
import { useT } from "./i18n/strings";
import s from "./Boot.module.css";

/**
 * Nothing renders until GET /jewelry/me has succeeded once: that call is what
 * proves the initData was signed by the Zardir bot for an allowed account.
 * The market board starts loading right after, once per app open.
 */
export function Boot({ children }: { children: ReactNode }) {
  const t = useT();
  const me = useMe();
  usePrices(me.isSuccess);

  // The Telegram account locale is the first-run default. suggestLang() is a
  // no-op once the user has chosen a language for themselves.
  const languageCode = me.data?.user.language_code;
  useEffect(() => suggestLang(languageCode), [languageCode]);

  if (me.isPending) return <Splash />;

  if (me.error) {
    const noCredential = !getInitData();
    const auth = me.error instanceof AuthError;
    return (
      <Splash
        title={t.errors.title}
        body={
          noCredential
            ? t.errors.notInTelegram
            : auth
              ? me.error.message
              : (messagesOf(me.error)[0] ?? t.errors.network)
        }
        isError
        action={
          // A rejected signature will not pass on a retry; a network blip will.
          !noCredential && !auth ? (
            <Button variant="secondary" onClick={() => me.refetch()}>
              {t.actions.retry}
            </Button>
          ) : undefined
        }
      />
    );
  }

  return <>{children}</>;
}

function Splash({
  title,
  body,
  action,
  isError,
}: {
  title?: string;
  body?: string;
  action?: ReactNode;
  isError?: boolean;
}) {
  const t = useT();
  const heading = title ?? t.appName;
  // Fraunces is the display face and it is Latin-only; a Persian heading set in
  // it would fall back mid-string, so the script chooses the font here exactly
  // as it does for an artwork title.
  const latin = isLatinScript(heading);
  return (
    <div className={s.boot}>
      <motion.div
        className={`${s.mark} paper`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={ui}
      >
        {!isError && <span className={s.markHalo} aria-hidden />}
        <Icon name={isError ? "alert" : "pieces"} size={32} />
      </motion.div>

      <motion.p
        className={[s.name, !latin && s.nameFa].filter(Boolean).join(" ")}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...slow, delay: 0.06 }}
      >
        {heading}
      </motion.p>

      <motion.p
        className={[s.body, isError && s.error].filter(Boolean).join(" ")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ ...slow, delay: 0.12 }}
      >
        {body ?? (insideTelegram ? t.loading : t.errors.notInTelegram)}
      </motion.p>

      {/* A drawn rule while the archive opens; an action once it cannot. */}
      {action ?? (isError ? null : <span className={s.rule} aria-hidden />)}
    </div>
  );
}
